import React, { forwardRef, type HTMLAttributes, ComponentProps } from 'react';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { Badge } from '@/shadcn/components/ui/badge';
import { cn } from '@/shadcn/lib/utils';

// Base components for the sheet
const PreviewSheetHeading = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-sm font-medium mb-2', className)} {...props}>
      {children}
    </h3>
  )
);
PreviewSheetHeading.displayName = 'PreviewSheetHeading';

interface PreviewSheetCaptionProps extends HTMLAttributes<HTMLParagraphElement> {
  caption?: string;
}

const PreviewSheetCaption = forwardRef<HTMLParagraphElement, PreviewSheetCaptionProps>(
  ({ className, caption, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-gray-700 whitespace-pre-wrap', className)}
      {...props}
    >
      {caption || '-'}
    </p>
  )
);
PreviewSheetCaption.displayName = 'PreviewSheetCaption';

// Info section components
interface PreviewSheetLabelProps extends HTMLAttributes<HTMLDivElement> {}

const PreviewSheetLabel = forwardRef<HTMLDivElement, PreviewSheetLabelProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm mb-1', className)} {...props} />
  )
);
PreviewSheetLabel.displayName = 'PreviewSheetLabel';

const PreviewSheetSection = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('space-y-1', className)} {...props}>
      {children}
    </div>
  )
);
PreviewSheetSection.displayName = 'PreviewSheetSection';

const PreviewSheetBadgeContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2', className)} {...props}>
      {children}
    </div>
  )
);
PreviewSheetBadgeContainer.displayName = 'PreviewSheetBadgeContainer';

const PreviewSheetEmpty = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children = '-', ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-gray-500', className)}
      {...props}
    >
      {children}
    </div>
  )
);
PreviewSheetEmpty.displayName = 'PreviewSheetEmpty';

const PreviewSheetBadge = ({ className, ...props }: ComponentProps<typeof Badge>) => (
  <Badge
    className={cn('px-2 py-2 rounded-lg text-xs font-normal text-white', className)}
    variant="secondary"
    {...props}
  />
);
PreviewSheetBadge.displayName = 'PreviewSheetBadge';

// Layout Components
const SheetGrid = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('grid grid-cols-1 md:grid-cols-3 h-full overflow-y-auto bg-white', className)}
      {...props}
    />
  )
);
SheetGrid.displayName = 'SheetGrid';

const SheetColumn = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('p-4', className)}
      {...props}
    />
  )
);
SheetColumn.displayName = 'SheetColumn';

const PreviewSheetContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-4', className)}
      {...props}
    />
  )
);
PreviewSheetContent.displayName = 'PreviewSheetContent';

const SheetTopBar = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center', className)}
      {...props}
    />
  )
);
SheetTopBar.displayName = 'SheetTopBar';

const MetricsGrid = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('grid grid-cols-4 gap-2', className)}
      {...props}
    />
  )
);
MetricsGrid.displayName = 'MetricsGrid';

const AccordionSections = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('space-y-4', className)}
      {...props}
    />
  )
);
AccordionSections.displayName = 'AccordionSections';

const TabContentContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('p-4', className)}
      {...props}
    />
  )
);
TabContentContainer.displayName = 'TabContentContainer';

interface SheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

interface SheetTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

const SheetTitle = React.forwardRef<HTMLHeadingElement, SheetTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <h2 ref={ref} className={cn('text-lg font-normal m-0', className)} {...props}>
        {children}
      </h2>
    );
  },
);

SheetTitle.displayName = 'SheetTitle';

const SheetHeader = React.forwardRef<HTMLDivElement, SheetHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-4 border-b flex justify-between items-center bg-white', className)} {...props}>
        {children}
      </div>
    );
  },
);

SheetHeader.displayName = 'SheetHeader';

// Composable SheetHeaderWithClose component
interface SheetHeaderWithCloseProps {
  title: string;
  onClose: () => void;
  closeAriaLabel?: string;
  className?: string;
}

const SheetHeaderWithClose = React.forwardRef<HTMLDivElement, SheetHeaderWithCloseProps>(
  ({ title, onClose, closeAriaLabel = 'Close', className }, ref) => {
    return (
      <SheetHeader ref={ref} className={className}>
        <SheetTitle>{title}</SheetTitle>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeAriaLabel}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 w-10"
        >
          <XmarkIcon className="h-4 w-4" />
        </button>
      </SheetHeader>
    );
  }
);

SheetHeaderWithClose.displayName = 'SheetHeaderWithClose';

export {
  SheetGrid,
  SheetColumn,
  PreviewSheetContent,
  SheetTopBar,
  MetricsGrid,
  AccordionSections,
  TabContentContainer,
  PreviewSheetHeading,
  PreviewSheetCaption,
  PreviewSheetLabel,
  PreviewSheetSection,
  PreviewSheetBadgeContainer,
  PreviewSheetBadge,
  PreviewSheetEmpty,
  SheetHeader,
  SheetTitle,
  SheetHeaderWithClose,
};

export type {
  PreviewSheetCaptionProps,
  PreviewSheetLabelProps,
  SheetHeaderProps,
  SheetTitleProps,
  SheetHeaderWithCloseProps,
};
